import { CodeBlock } from './CodeBlock';

const EXAMPLE = `import * as cdk from 'aws-cdk-lib';
import { createStack, loadManifest } from 'fargate-deployer';

const app = new cdk.App();

// Your own infrastructure, unchanged.
const infra = new MyInfraStack(app, 'my-infra');

// The service, from the same manifest CI would use.
const config = loadManifest('deploy/production.yaml');

// Anything CDK owns can be patched in — these are tokens, not strings.
config.task.environment.QUEUE_URL = infra.queue.queueUrl;
config.roles.taskRoleArn = infra.taskRole.roleArn;

createStack({ app, config, image: process.env.IMAGE! });`;

const SYNTHESISED = `"Environment": [
  { "Name": "QUEUE_URL",
    "Value": { "Fn::ImportValue": "my-infra:ExportsOutputRefJobs..." } }
]`;

/**
 * Construct mode.
 *
 * Present because the rest of the page pitches the no-CDK path, and someone who
 * already owns a CDK app would otherwise conclude the tool is not for them —
 * when in fact it has a first-class route for exactly that case.
 */
export function ConstructMode() {
  return (
    <section id="construct-mode">
      <h2>Already have a CDK app?</h2>
      <p className="lede">
        Import the stack into the app you have, rather than deploying a second one. The manifest
        carries the forty lines that are the same every time; TypeScript carries the parts that have
        to reference real resources.
      </p>

      <div className="callout" style={{ marginBottom: '1.5rem' }}>
        <strong>This is not just a preference.</strong> The CLI synthesises its own{' '}
        <em>single-stack</em> app, so any other stacks in your repository are invisible to it — they
        do not deploy, and nothing warns you. If you own CDK already, this is the mode you want.
      </div>

      <CodeBlock code={EXAMPLE} />

      <p style={{ marginTop: '1.25rem' }}>
        Then one <code>cdk deploy --all</code> covers both. Those tokens resolve at synth time into
        a real cross-stack reference:
      </p>

      <CodeBlock code={SYNTHESISED} />

      <p className="muted small" style={{ marginTop: '1rem' }}>
        CloudFormation creates the export on your stack, the import on the service's, and a
        dependency between them — so deploy order is correct, and your queue cannot be deleted while
        the service still references it.
      </p>

      <p className="small" style={{ marginTop: '1.25rem' }}>
        The stack joins your app as a <strong>sibling</strong>, not nested inside one of yours.
        Construct mode builds nothing, so <code>image</code> is a URI from whatever pipeline you
        already run.{' '}
        <a href="https://github.com/FutureMoney/fargate-deployer/blob/main/docs/construct-mode.md">
          Full guide ↗
        </a>
      </p>
    </section>
  );
}
